// P/L 画面の日付・期間・チャートバケット計算（純関数のみ・DOM/Reactに依存しない）。
// design/STOQ_dashboard.dc.html の idx/dt/md/mdw/range/chart を ISO日付文字列ベースに移植したもの。

import type { DailyRow, Mall } from "../types/pl";

export type PeriodMode = "day" | "month" | "range";

export interface Period {
  from: string;
  to: string;
}

const MS_DAY = 86400000;

function toUtcMs(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function fromUtcMs(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

export function addDays(dateStr: string, n: number): string {
  return fromUtcMs(toUtcMs(dateStr) + n * MS_DAY);
}

export function diffDays(a: string, b: string): number {
  return Math.round((toUtcMs(b) - toUtcMs(a)) / MS_DAY);
}

export function md(dateStr: string): string {
  const [, m, d] = dateStr.split("-");
  return `${Number(m)}/${Number(d)}`;
}

const WEEKDAY_JA = ["日", "月", "火", "水", "木", "金", "土"];

export function weekday(dateStr: string): number {
  return new Date(toUtcMs(dateStr)).getUTCDay();
}

export function mdw(dateStr: string): string {
  return `${md(dateStr)}（${WEEKDAY_JA[weekday(dateStr)]}）`;
}

export function addMonths(monthStr: string, n: number): string {
  let [y, m] = monthStr.split("-").map(Number);
  m += n;
  while (m < 1) {
    m += 12;
    y -= 1;
  }
  while (m > 12) {
    m -= 12;
    y += 1;
  }
  return `${y}-${String(m).padStart(2, "0")}`;
}

export function monthFirstDay(monthStr: string): string {
  return `${monthStr}-01`;
}

export function monthLastDay(monthStr: string): string {
  const [y, m] = monthStr.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${monthStr}-${String(last).padStart(2, "0")}`;
}

export function clampDate(dateStr: string, max: string): string {
  return dateStr > max ? max : dateStr;
}

/** KPI・P/L表・商品/広告タブが対象にする「実期間」。 */
export function mainRange(
  mode: PeriodMode,
  sel: { day: string; month: string; from: string; to: string },
  today: string
): Period {
  if (mode === "day") return { from: sel.day, to: sel.day };
  if (mode === "month") {
    const first = monthFirstDay(sel.month);
    const last = monthLastDay(sel.month);
    return { from: first, to: clampDate(last, today) };
  }
  let [a, b] = [sel.from, sel.to];
  if (a > b) [a, b] = [b, a];
  return { from: a, to: b };
}

/** 比較期間（前日 / 前月同日数 / 直前の同じ長さの期間）。 */
export function compareRange(mode: PeriodMode, sel: { month: string }, main: Period): Period | null {
  if (mode === "day") {
    const prev = addDays(main.from, -1);
    return { from: prev, to: prev };
  }
  if (mode === "month") {
    const prevMonth = addMonths(sel.month, -1);
    const prevFrom = monthFirstDay(prevMonth);
    const elapsed = diffDays(main.from, main.to); // 0-indexed
    let prevTo = addDays(prevFrom, elapsed);
    const prevMonthLast = monthLastDay(prevMonth);
    if (prevTo > prevMonthLast) prevTo = prevMonthLast;
    return { from: prevFrom, to: prevTo };
  }
  const len = diffDays(main.from, main.to) + 1;
  const prevTo = addDays(main.from, -1);
  const prevFrom = addDays(prevTo, -(len - 1));
  return { from: prevFrom, to: prevTo };
}

/** 概要/広告タブのチャートに使う表示ウィンドウ（KPI期間とは別に広め）。 */
export function chartRange(mode: PeriodMode, sel: { day: string; month: string }, main: Period): Period {
  if (mode === "day") return { from: addDays(sel.day, -13), to: sel.day };
  if (mode === "month") return { from: monthFirstDay(sel.month), to: monthLastDay(sel.month) };
  return { from: main.from, to: main.to };
}

export interface ChartBar {
  a: string; // バケット開始日
  b: string; // バケット終了日
  values: Partial<Record<Mall, number>>;
  profit: number;
  has: boolean;
}

/**
 * daily配列（GET /pl(/ads) の daily フィールド）から表示バケットを作る。
 * 期間が62日を超える場合は週次バケット（design README §「期間指定」）。
 */
export function bucketChart(
  daily: DailyRow[],
  from: string,
  to: string,
  metric: "sales" | "ad_spend",
  mallFilter: Mall | null
): { bars: ChartBar[]; step: number } {
  const len = Math.max(1, diffDays(from, to) + 1);
  const step = len > 62 ? 7 : 1;
  const nb = Math.ceil(len / step);
  const byDate = new Map(daily.map((d) => [d.date, d]));
  const malls: Mall[] = mallFilter ? [mallFilter] : ["shopify", "rakuten", "amazon"];
  const bars: ChartBar[] = [];
  for (let k = 0; k < nb; k++) {
    const a = addDays(from, k * step);
    const bEnd = addDays(from, Math.min(len - 1, k * step + step - 1));
    const values: Partial<Record<Mall, number>> = {};
    let profit = 0;
    let has = false;
    const span = diffDays(a, bEnd);
    for (let di = 0; di <= span; di++) {
      const date = addDays(a, di);
      const row = byDate.get(date);
      if (!row) continue;
      has = true;
      for (const mall of malls) {
        const pt = row.malls[mall];
        if (!pt) continue;
        const v = metric === "sales" ? pt.sales : pt.ad_spend;
        values[mall] = (values[mall] ?? 0) + v;
        profit += pt.contribution;
      }
    }
    bars.push({ a, b: bEnd, values, profit, has });
  }
  return { bars, step };
}
